<template>
  <div class="business-metrics">
    <div class="chart-container">
      <h5><i class="fal fa-chart-line text-success"></i> Métricas de Negocio</h5>

      <!-- Impacto en Pensionados -->
      <div class="impact-section">
        <h6><i class="fal fa-users text-primary"></i> Impacto en Pensionados</h6>
        <div class="row">
          <div class="col-md-3" v-for="(valor, metrica) in satisfaccionMetrics.impacto_pensionados" :key="metrica">
            <div class="impact-card">
              <div class="impact-icon">
                <i :class="getImpactIcon(metrica)"></i>
              </div>
              <div class="impact-content">
                <div class="impact-value">{{ formatImpactValue(valor, metrica) }}</div>
                <div class="impact-label">{{ getImpactLabel(metrica) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Eficiencia Operativa -->
      <div class="efficiency-section">
        <h6><i class="fal fa-cogs text-warning"></i> Eficiencia Operativa</h6>
        <div class="row">
          <div class="col-md-3" v-for="(valor, metrica) in satisfaccionMetrics.eficiencia_operativa" :key="metrica">
            <div class="efficiency-card">
              <div class="efficiency-icon">
                <i :class="getEfficiencyIcon(metrica)"></i>
              </div>
              <div class="efficiency-content">
                <div class="efficiency-value">{{ formatEfficiencyValue(valor, metrica) }}</div>
                <div class="efficiency-label">{{ getEfficiencyLabel(metrica) }}</div>
                <div class="efficiency-trend" v-if="metrica === 'roi_sistema'">
                  <span class="trend-indicator" :class="getROITrend(valor)">
                    <i :class="getROITrendIcon(valor)"></i>
                    {{ getROITrendText(valor) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Indicadores de Negocio -->
      <div class="indicators-section">
        <h6><i class="fal fa-chart-bar text-info"></i> Indicadores Clave de Negocio</h6>
        <div class="row">
          <div class="col-md-3" v-for="(valor, indicador) in satisfaccionMetrics.indicadores_negocio" :key="indicador">
            <div class="indicator-card">
              <div class="indicator-icon">
                <i :class="getIndicatorIcon(indicador)"></i>
              </div>
              <div class="indicator-content">
                <div class="indicator-value">{{ formatIndicatorValue(valor, indicador) }}</div>
                <div class="indicator-label">{{ getIndicatorLabel(indicador) }}</div>
                <div class="indicator-status" :class="getIndicatorStatus(valor, indicador)">
                  {{ getIndicatorStatusText(valor, indicador) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Análisis de ROI -->
      <div class="roi-analysis">
        <div class="row">
          <div class="col-md-6">
            <div class="roi-card">
              <h6><i class="fal fa-calculator text-success"></i> Análisis de ROI</h6>
              <div class="roi-metrics">
                <div class="roi-metric">
                  <div class="metric-label">ROI del Sistema</div>
                  <div class="metric-value">{{ (satisfaccionMetrics.eficiencia_operativa && satisfaccionMetrics.eficiencia_operativa.roi_sistema) || 0 }}x</div>
                  <div class="metric-description">
                    Por cada peso invertido, se generan {{ (satisfaccionMetrics.eficiencia_operativa && satisfaccionMetrics.eficiencia_operativa.roi_sistema) || 0 }} pesos en valor
                  </div>
                </div>
                <div class="roi-comparison">
                  <div class="comparison-item">
                    <div class="comparison-label">Benchmark del Sector</div>
                    <div class="comparison-value">${{ (satisfaccionMetrics.eficiencia_operativa && satisfaccionMetrics.eficiencia_operativa.benchmark_sector) || 0 }}</div>
                  </div>
                  <div class="comparison-item">
                    <div class="comparison-label">Costo Actual</div>
                    <div class="comparison-value">${{ (satisfaccionMetrics.eficiencia_operativa && satisfaccionMetrics.eficiencia_operativa.costo_por_solicitud) || 0 }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="opportunities-card">
              <h6><i class="fal fa-lightbulb text-warning"></i> Oportunidades de Ahorro</h6>
              <div class="opportunities-content">
                <div class="opportunity-item">
                  <div class="opportunity-label">Ahorro Potencial</div>
                  <div class="opportunity-value">{{ (satisfaccionMetrics.eficiencia_operativa && satisfaccionMetrics.eficiencia_operativa.oportunidades_ahorro) || 0 }}%</div>
                  <div class="opportunity-bar">
                    <div class="opportunity-progress"
                         :style="{ width: ((satisfaccionMetrics.eficiencia_operativa && satisfaccionMetrics.eficiencia_operativa.oportunidades_ahorro) || 0) + '%' }"></div>
                  </div>
                </div>
                <div class="savings-breakdown">
                  <div class="savings-item">
                    <span class="savings-label">Automatización:</span>
                    <span class="savings-value">15%</span>
                  </div>
                  <div class="savings-item">
                    <span class="savings-label">Optimización de Procesos:</span>
                    <span class="savings-value">25%</span>
                  </div>
                  <div class="savings-item">
                    <span class="savings-label">Reducción de Errores:</span>
                    <span class="savings-value">10%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BusinessMetrics',
  props: {
    satisfaccionMetrics: {
      type: Object,
      default: () => ({})
    }
  },
  methods: {
    getImpactIcon(metrica) {
      const iconMap = {
        'tiempo_espera_promedio': 'fal fa-clock',
        'tasa_resolucion': 'fal fa-check-circle',
        'satisfaccion_general': 'fal fa-smile',
        'retencion': 'fal fa-user-lock'
      }
      return iconMap[metrica] || 'fal fa-chart-line'
    },

    getEfficiencyIcon(metrica) {
      const iconMap = {
        'costo_por_solicitud': 'fal fa-dollar-sign',
        'roi_sistema': 'fal fa-chart-line',
        'benchmark_sector': 'fal fa-balance-scale',
        'oportunidades_ahorro': 'fal fa-piggy-bank'
      }
      return iconMap[metrica] || 'fal fa-cogs'
    },

    getIndicatorIcon(indicador) {
      const iconMap = {
        'solicitudes_por_dia': 'fal fa-calendar-day',
        'resolucion_primera_instancia': 'fal fa-first-aid',
        'escalamientos_porcentaje': 'fal fa-level-up-alt',
        'tiempo_resolucion_objetivo': 'fal fa-target'
      }
      return iconMap[indicador] || 'fal fa-chart-bar'
    },

    formatImpactValue(valor, metrica) {
      if (metrica === 'tiempo_espera_promedio') return `${valor}h`
      if (metrica === 'tasa_resolucion') return `${valor}%`
      if (metrica === 'satisfaccion_general') return valor
      if (metrica === 'retencion') return `${valor}%`
      return valor
    },

    formatEfficiencyValue(valor, metrica) {
      if (metrica === 'costo_por_solicitud') return `$${valor}`
      if (metrica === 'roi_sistema') return `${valor}x`
      if (metrica === 'benchmark_sector') return `$${valor}`
      if (metrica === 'oportunidades_ahorro') return `${valor}%`
      return valor
    },

    formatIndicatorValue(valor, indicador) {
      if (indicador === 'solicitudes_por_dia') return valor
      if (indicador === 'resolucion_primera_instancia') return `${valor}%`
      if (indicador === 'escalamientos_porcentaje') return `${valor}%`
      if (indicador === 'tiempo_resolucion_objetivo') return `${valor}h`
      return valor
    },

    getImpactLabel(metrica) {
      const labelMap = {
        'tiempo_espera_promedio': 'Tiempo de Espera Promedio',
        'tasa_resolucion': 'Tasa de Resolución',
        'satisfaccion_general': 'Satisfacción General',
        'retencion': 'Tasa de Retención'
      }
      return labelMap[metrica] || metrica
    },

    getEfficiencyLabel(metrica) {
      const labelMap = {
        'costo_por_solicitud': 'Costo por Solicitud',
        'roi_sistema': 'ROI del Sistema',
        'benchmark_sector': 'Benchmark del Sector',
        'oportunidades_ahorro': 'Oportunidades de Ahorro'
      }
      return labelMap[metrica] || metrica
    },

    getIndicatorLabel(indicador) {
      const labelMap = {
        'solicitudes_por_dia': 'Solicitudes por Día',
        'resolucion_primera_instancia': 'Resolución Primera Instancia',
        'escalamientos_porcentaje': 'Porcentaje de Escalamientos',
        'tiempo_resolucion_objetivo': 'Tiempo Objetivo de Resolución'
      }
      return labelMap[indicador] || indicador
    },

    getROITrend(roi) {
      if (roi > 3) return 'trend-excellent'
      if (roi > 2) return 'trend-good'
      if (roi > 1) return 'trend-average'
      return 'trend-poor'
    },

    getROITrendIcon(roi) {
      if (roi > 3) return 'fal fa-arrow-up'
      if (roi > 2) return 'fal fa-arrow-up'
      if (roi > 1) return 'fal fa-minus'
      return 'fal fa-arrow-down'
    },

    getROITrendText(roi) {
      if (roi > 3) return 'Excelente'
      if (roi > 2) return 'Bueno'
      if (roi > 1) return 'Promedio'
      return 'Necesita Mejora'
    },

    getIndicatorStatus(valor, indicador) {
      if (indicador === 'resolucion_primera_instancia') {
        if (valor >= 80) return 'status-excellent'
        if (valor >= 60) return 'status-good'
        return 'status-poor'
      }
      if (indicador === 'escalamientos_porcentaje') {
        if (valor <= 10) return 'status-excellent'
        if (valor <= 20) return 'status-good'
        return 'status-poor'
      }
      return 'status-default'
    },

    getIndicatorStatusText(valor, indicador) {
      if (indicador === 'resolucion_primera_instancia') {
        if (valor >= 80) return 'Excelente'
        if (valor >= 60) return 'Bueno'
        return 'Necesita Mejora'
      }
      if (indicador === 'escalamientos_porcentaje') {
        if (valor <= 10) return 'Excelente'
        if (valor <= 20) return 'Bueno'
        return 'Necesita Mejora'
      }
      return 'Normal'
    }
  }
}
</script>

<style scoped>
.business-metrics {
  margin-bottom: 30px;
}

.chart-container {
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.chart-container h5 {
  color: #495057;
  margin-bottom: 20px;
  font-weight: 600;
}

.impact-section,
.efficiency-section,
.indicators-section {
  margin-bottom: 30px;
}

.impact-section h6,
.efficiency-section h6,
.indicators-section h6 {
  color: #495057;
  margin-bottom: 15px;
  font-weight: 600;
}

.impact-card,
.efficiency-card,
.indicator-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 15px;
  transition: transform 0.2s ease;
}

.impact-card:hover,
.efficiency-card:hover,
.indicator-card:hover {
  transform: translateY(-2px);
}

.impact-icon,
.efficiency-icon,
.indicator-icon {
  margin-right: 15px;
  flex-shrink: 0;
  font-size: 2rem;
  color: #1f4e79;
}

.impact-content,
.efficiency-content,
.indicator-content {
  flex-grow: 1;
}

.impact-value,
.efficiency-value,
.indicator-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #495057;
  margin-bottom: 5px;
}

.impact-label,
.efficiency-label,
.indicator-label {
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 5px;
}

.efficiency-trend {
  margin-top: 5px;
}

.trend-indicator {
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 12px;
  display: inline-block;
}

.trend-excellent { background-color: #d4edda; color: #155724; }
.trend-good { background-color: #d1ecf1; color: #0c5460; }
.trend-average { background-color: #fff3cd; color: #856404; }
.trend-poor { background-color: #f8d7da; color: #721c24; }

.indicator-status {
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 12px;
  display: inline-block;
}

.status-excellent { background-color: #d4edda; color: #155724; }
.status-good { background-color: #d1ecf1; color: #0c5460; }
.status-poor { background-color: #f8d7da; color: #721c24; }
.status-default { background-color: #e9ecef; color: #495057; }

.roi-analysis {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.roi-card,
.opportunities-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
}

.roi-card h6,
.opportunities-card h6 {
  color: #495057;
  margin-bottom: 15px;
  font-weight: 600;
}

.roi-metrics {
  margin-bottom: 20px;
}

.roi-metric {
  text-align: center;
  margin-bottom: 20px;
}

.metric-label {
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 5px;
}

.metric-value {
  font-size: 2rem;
  font-weight: bold;
  color: #28a745;
  margin-bottom: 10px;
}

.metric-description {
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.4;
}

.roi-comparison {
  display: flex;
  justify-content: space-around;
  gap: 20px;
}

.comparison-item {
  text-align: center;
}

.comparison-label {
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 5px;
}

.comparison-value {
  font-size: 1.2rem;
  font-weight: bold;
  color: #495057;
}

.opportunities-content {
  text-align: center;
}

.opportunity-item {
  margin-bottom: 20px;
}

.opportunity-label {
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 5px;
}

.opportunity-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #28a745;
  margin-bottom: 10px;
}

.opportunity-bar {
  height: 10px;
  background-color: #e9ecef;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 15px;
}

.opportunity-progress {
  height: 100%;
  background: linear-gradient(90deg, #28a745, #20c997);
  transition: width 0.3s ease;
}

.savings-breakdown {
  text-align: left;
}

.savings-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
}

.savings-item:last-child {
  border-bottom: none;
}

.savings-label {
  font-size: 0.8rem;
  color: #6c757d;
}

.savings-value {
  font-weight: 600;
  color: #495057;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .impact-value,
  .efficiency-value,
  .indicator-value {
    font-size: 1.2rem;
  }

  .impact-label,
  .efficiency-label,
  .indicator-label {
    font-size: 0.8rem;
  }

  .metric-value {
    font-size: 1.5rem;
  }

  .opportunity-value {
    font-size: 1.2rem;
  }

  .roi-comparison {
    flex-direction: column;
    gap: 10px;
  }
}
</style>
